/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
~ Copyright 2020 Adobe Systems Incorporated
~
~ Licensed under the Apache License, Version 2.0 (the "License");
~ you may not use this file except in compliance with the License.
~ You may obtain a copy of the License at
~
~     http://www.apache.org/licenses/LICENSE-2.0
~
~ Unless required by applicable law or agreed to in writing, software
~ distributed under the License is distributed on an "AS IS" BASIS,
~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
~ See the License for the specific language governing permissions and
~ limitations under the License.
~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React, { useEffect, useId, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  RatingAndReviews as UIFoundationsRatingAndReviews,
  RatingAndReviewsTypes
} from '@dcxwam/dcx-wam-ui-foundations'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import ConfigureAndBuy from '../ConfigureAndBuy/ConfigureAndBuy'
import { RatingAndReviewsModelProps, RatingAndReviewsTypesModel } from './types'
import RatingAndReviewsEditConfig from './RatingAndReviewsEditConfig'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'

const RatingAndReviews: React.FC<
  EditableComponentPropsType<RatingAndReviewsModelProps>
> = withComponentRefresh(({ ...props }) => {
  const { gcode, type, ...rest } = props.model
  const editableProps = { ...props, model: { ...rest } }

  const [productId, setProductId] = useState<string | undefined>(gcode)
  const id = useId()

  useEffect(() => {
    ConfigureAndBuy.subscribeToGcodeSetup(id, (setupGcode: string) => {
      setProductId(setupGcode)
    })
    return () => {
      ConfigureAndBuy.unsubscribeFromGcodeSetup(id)
    }
  }, [])

  let ratingType = RatingAndReviewsTypes.InlineRating
  switch (type) {
    case RatingAndReviewsTypesModel.Reviews:
      ratingType = RatingAndReviewsTypes.Reviews
      break
    case RatingAndReviewsTypesModel.RatingSummary:
      ratingType = RatingAndReviewsTypes.RatingSummary
      break
    case RatingAndReviewsTypesModel.InlineRating:
    default:
      break
  }

  const config = {
    type: ratingType,
    productId
  }

  return (
    <EditableComponent {...editableProps}>
      {!RatingAndReviewsEditConfig.isEmpty(props) ? (
        <UIFoundationsRatingAndReviews {...config} />
      ) : (
        RatingAndReviewsEditConfig.emptyLabel
      )}
    </EditableComponent>
  )
})

export default RatingAndReviews
